import { useQuery } from '@tanstack/react-query';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate, formatMinor } from '../../lib/format';
import type { Payout, PayoutStatus } from '../../lib/types';
import { Badge, DataTable, PageHeader, Spinner } from '../../components/ui';

const STATUS_TONE: Record<PayoutStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'neutral',
  approved: 'info',
  in_transit: 'warning',
  paid: 'success',
  failed: 'danger',
  cancelled: 'danger',
};

/** Read-only, per the brief: "Payouts & statements, invoices from Loctopus - read-only list/detail
 * views." Statements download from `statementPdfR2Key` once apps/api serves a signed URL for it;
 * that URL shape isn't in docs/API.md yet, so the download link here is a placeholder. */
export function PayoutsPage() {
  const payoutsQuery = useQuery({
    queryKey: ['payouts'],
    queryFn: () => api.payouts.list({ limit: 100 }),
  });

  return (
    <div>
      <PageHeader
        title="Payouts"
        description="Settlement payouts from Loctopus to your bank account."
      />
      {payoutsQuery.isLoading ? (
        <Spinner />
      ) : payoutsQuery.error ? (
        <ErrorSummary error={toParsedError(payoutsQuery.error)} />
      ) : (
        <DataTable<Payout>
          columns={[
            {
              key: 'period',
              header: 'Period',
              render: (p) => `${formatDate(p.periodStart)} - ${formatDate(p.periodEnd)}`,
            },
            {
              key: 'amount',
              header: 'Amount',
              render: (p) => formatMinor(p.amountMinor, p.currency),
            },
            {
              key: 'reserve',
              header: 'Reserve held',
              render: (p) => formatMinor(p.reserveHeldMinor, p.currency),
            },
            {
              key: 'status',
              header: 'Status',
              render: (p) => <Badge tone={STATUS_TONE[p.status]}>{p.status}</Badge>,
            },
            { key: 'paidAt', header: 'Paid at', render: (p) => formatDate(p.paidAt) },
            {
              key: 'statement',
              header: 'Statement',
              render: (p) =>
                p.statementPdfR2Key ? (
                  <a
                    className="text-brand-accent underline"
                    href={`/dashboard/payouts/${p.id}/statement`}
                  >
                    Download
                  </a>
                ) : (
                  '-'
                ),
            },
          ]}
          rows={payoutsQuery.data?.data ?? []}
          getRowKey={(p) => p.id}
          emptyMessage="No payouts yet."
        />
      )}
    </div>
  );
}
